import { Button, CircularProgress } from '@mui/material';
import React, { useRef, useState } from 'react';

import { BottomSheet } from '../../../../../../components/molecules/BottomSheet';
import { StatusConfirmStep } from './StatusConfirmStep';
import { StatusSelectionStep } from './StatusSelectionStep';
import {
  MobileStatusUpdateDialogProps,
  StatusUpdateStep,
  StatusSelection,
} from './MobileStatusUpdateDialog.props';
import { LoadingContainer } from './MobileStatusUpdateDialog.styles';
import {
  processSetParameterFields,
  prePopulateAllocationFields,
  buildInterceptedFormsObject,
  validateRequiredFields,
  hasBlockInteraction,
  getBlockMessage,
} from '../../../../../../utils/mobile-interactions';
import { InteractionFormData, ServiceType } from '../../../../../../@types/service.type';
import { httpClient, HttpMethod } from '../../../../../../api/http-client';
import { useSubmitFormsDataWithOriginalPayload } from '../../../../../../queries/interactions-query';
import { useSearchAllocationNodes } from '../../../../../../queries/live-search-query';
import { useUpdateServiceAllocation } from '../../../../../../queries/service-query';

interface StatusCheckResponse {
  interactionFormData?: InteractionFormData[];
  actionTriggers?: any[];
  isCheckin?: boolean;
  multiOnSiteSuggestionsAvailable?: boolean;
}

const STEP_TITLES: Record<StatusUpdateStep, string> = {
  'select-status': 'Update Status',
  'loading-interactions': 'Checking Requirements...',
  confirm: 'Confirm Status Change',
  submitting: 'Updating...',
};

export const MobileStatusUpdateDialog: React.FC<MobileStatusUpdateDialogProps> = ({
  open,
  onClose,
  service,
  nextStatuses,
  onStatusChanged,
}) => {
  const [step, setStep] = useState<StatusUpdateStep>('select-status');
  const [selectedStatus, setSelectedStatus] = useState<StatusSelection | null>(null);
  const [notes, setNotes] = useState('');
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [interactions, setInteractions] = useState<InteractionFormData[]>([]);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Store the raw response from the interaction check for building the submit payload
  const interactionResponseRef = useRef<StatusCheckResponse | null>(null);

  const submitFormsMutation = useSubmitFormsDataWithOriginalPayload(service.id);
  const searchAllocationNodes = useSearchAllocationNodes();
  const updateServiceAllocation = useUpdateServiceAllocation(service.id);

  const availableStatuses = nextStatuses?.possibleNextStatuses || [];

  const handleStatusSelect = async (status: ServiceType): Promise<void> => {
    setSelectedStatus({ code: status.code, name: status.name });
    setStep('loading-interactions');
    setError(null);

    try {
      // Call API directly — avoids useSaveServiceStatus's onSuccess which
      // invalidates queries and delays resolution even though no status changed.
      const response = await httpClient<StatusCheckResponse>(
        `/services/${service.id}/status`,
        HttpMethod.Put,
        { nextStatus: status.code, note: '', byPassInteractionCheck: false }
      );

      const interactionData = response?.interactionFormData || [];
      interactionResponseRef.current = response;
      setInteractions(interactionData);
      setIsBlocked(hasBlockInteraction(interactionData));

      if (interactionData.length > 0) {
        const initialValues = await prePopulateAllocationFields(
          interactionData,
          searchAllocationNodes
        );
        if (Object.keys(initialValues).length > 0) {
          setFormValues((prev) => ({ ...prev, ...initialValues }));
        }
      }

      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check status requirements');
      setStep('select-status');
    }
  };

  const handleBlockBypass = (): void => {
    setIsBlocked(false);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!selectedStatus) return;

    if (isBlocked) {
      setError(getBlockMessage(interactions));
      return;
    }

    const missingRequired = validateRequiredFields(interactions, formValues);
    if (missingRequired) {
      setError(`Please fill in required field: ${missingRequired}`);
      return;
    }

    setStep('submitting');
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await processSetParameterFields(interactions, formValues, service.id, {
        updateServiceAllocation,
        searchAllocationNodes,
      });
      if (!result.success) {
        setError(result.error || 'Failed to process fields');
        setStep('confirm');
        setIsSubmitting(false);
        return;
      }

      // Build the InterceptedFormsObject in the same format the desktop
      // AxiosInterceptor produces, with user-filled field values injected.
      const formsObject = buildInterceptedFormsObject(
        service.id,
        selectedStatus.code,
        interactions,
        formValues,
        notes.trim() || '',
        interactionResponseRef.current
      );

      await submitFormsMutation.mutateAsync(formsObject);
      await onStatusChanged();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
      setStep('confirm');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (interactionId: number, fieldId: number, value: any): void => {
    setFormValues((prev) => ({ ...prev, [`${interactionId}-${fieldId}`]: value }));
  };

  const resetState = (): void => {
    setStep('select-status');
    setSelectedStatus(null);
    setNotes('');
    setFormValues({});
    setError(null);
    setInteractions([]);
    setIsBlocked(false);
    interactionResponseRef.current = null;
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const isPending = isSubmitting || submitFormsMutation.isPending;

  const footer =
    step === 'confirm' ? (
      <>
        <Button onClick={resetState} variant="outlined" fullWidth>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={isBlocked || isPending}
        >
          {isPending ? 'Updating...' : 'Submit'}
        </Button>
      </>
    ) : undefined;

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      height="large"
      title={STEP_TITLES[step]}
      subtitle={`Current: ${service.serviceStatus?.name || 'Unknown'}`}
      footer={footer}
    >
      {step === 'select-status' && (
        <StatusSelectionStep
          currentStatusName={service.serviceStatus?.name || 'Unknown'}
          availableStatuses={availableStatuses}
          error={error}
          onSelectStatus={handleStatusSelect}
        />
      )}

      {(step === 'loading-interactions' || step === 'submitting') && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}

      {step === 'confirm' && selectedStatus && (
        <StatusConfirmStep
          selectedStatus={selectedStatus}
          notes={notes}
          onNotesChange={setNotes}
          error={error}
          interactions={interactions}
          formValues={formValues}
          onFieldChange={handleFieldChange}
          onBlockBypass={handleBlockBypass}
          service={service}
        />
      )}
    </BottomSheet>
  );
};
